import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Typography } from 'antd';
import { BarcodeOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import { useLocation } from 'react-router-dom';

import { Flex1, TitleWithExtra } from '../components/layout/layoutElements';
import { JobLocationSection } from '../forms/shipments/newShipment/newShipmentElements';
import { LINK_JOB_TO_DEVICE_MUTATION } from '../app/graphql/jobFormQueries';
import ProcessingModal from '../components/dialogs/ProcessingModal';
import { OrderSuccessDialog as SuccessDialog } from '../dialogs/shipments/newShipmentDialogs';
import { SimpleConfirmDialog } from '../components/dialogs/SimpleConfirmDialog';
import {
  DEVICE_TYPES,
  buildLinkDeviceInput,
  parseLinkDeviceContext,
  validateLinkDeviceForm,
} from './linkDeviceToJobUtils';
import { useLinkDeviceToJobPageStyles } from './LinkDeviceToJobPage.styles';

const { Text } = Typography;

const VIDEO_CONSTRAINTS = {
  facingMode: 'environment',
  width: { ideal: 1280 },
  height: { ideal: 720 },
  advanced: [{ zoom: 2.0 }, { focusMode: 'continuous' }, { brightness: 1.0 }],
};

const TORCH_DELAY_MS = 500;

const INITIAL_FORM_STATE = {
  deviceType: undefined,
  deviceId: undefined,
};

const INITIAL_ERRORS_STATE = {
  deviceId: false,
  deviceType: false,
};

function BarcodeScanner({ torchOn, onScanUpdate }) {
  const { styles } = useLinkDeviceToJobPageStyles();
  return (
    <div className={styles.scannerWrapper}>
      <BarcodeScannerComponent
        width="100%"
        height="100%"
        facingMode="environment"
        torch={torchOn}
        delay={100}
        videoConstraints={VIDEO_CONSTRAINTS}
        onUpdate={onScanUpdate}
      />
      <div className={styles.scannerOverlay}>
        <div className={styles.scannerFrame} />
      </div>
    </div>
  );
}

function ScannerHints() {
  const { styles } = useLinkDeviceToJobPageStyles();
  return (
    <div className={styles.scannerHints}>
      <Text type="secondary">
        <FormattedMessage id="linkDeviceToJob.scanHint" />
      </Text>
    </div>
  );
}

function DeviceForm({
  form,
  errors,
  deviceTypeOptions,
  onFormChange,
  onOpenScanner,
}) {
  const { styles } = useLinkDeviceToJobPageStyles();
  const handleInputChange = fieldName => event => {
    onFormChange({ ...form, [fieldName]: event.target.value });
  };

  const handleSelectChange = value => {
    onFormChange({ ...form, deviceType: value });
  };

  const renderBarcodeSuffix = fieldName => (
    <BarcodeOutlined
      className={styles.barcodeIcon}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onOpenScanner(fieldName);
      }}
    />
  );

  return (
    <>
      <JobLocationSection
        titleId="linkDeviceToJob.deviceType"
        dividerClassName={styles.hiddenDivider}
      >
        <Select
          value={form.deviceType}
          onChange={handleSelectChange}
          status={errors.deviceType ? 'error' : ''}
          options={deviceTypeOptions}
        />
      </JobLocationSection>

      <JobLocationSection
        titleId="linkDeviceToJob.deviceId"
        dividerClassName={styles.hiddenDivider}
      >
        <Input
          value={form.deviceId}
          onChange={handleInputChange('deviceId')}
          status={errors.deviceId ? 'error' : ''}
          suffix={renderBarcodeSuffix('deviceId')}
        />
      </JobLocationSection>
    </>
  );
}

function useQueryParams() {
  const location = useLocation();

  return useMemo(
    () => parseLinkDeviceContext(location.search),
    [location.search]
  );
}

function useTorchControl(showCamera, cameraReady) {
  const [torchOn, setTorchOn] = useState(false);

  useEffect(() => {
    if (!showCamera) {
      setTorchOn(false);
      return undefined;
    }

    if (!cameraReady) {
      return undefined;
    }

    const torchTimeout = setTimeout(() => {
      setTorchOn(true);
    }, TORCH_DELAY_MS);

    return () => clearTimeout(torchTimeout);
  }, [showCamera, cameraReady]);

  return torchOn;
}

export default function LinkDeviceToJobPage() {
  const { styles } = useLinkDeviceToJobPageStyles();
  const { formatMessage: f } = useIntl();
  const context = useQueryParams();
  const [mutate, { loading }] = useMutation(LINK_JOB_TO_DEVICE_MUTATION);

  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [activeField, setActiveField] = useState('');
  const [submitResult, setSubmitResult] = useState(undefined);
  const [isJobNumberMissing, setIsJobNumberMissing] = useState(false);

  const torchOn = useTorchControl(showCamera, cameraReady);

  const deviceTypeOptions = DEVICE_TYPES.map(type => ({
    label: f({ id: type.labelId }),
    value: type.value,
  }));

  useEffect(() => {
    if (!showCamera) {
      setCameraReady(false);
    }
  }, [showCamera]);

  useEffect(() => {
    if (!context.jobNumber) {
      setIsJobNumberMissing(true);
    }
  }, [context.jobNumber]);

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM_STATE);
    setActiveField('');
    setShowCamera(false);
    setErrors(INITIAL_ERRORS_STATE);
    setIsJobNumberMissing(false);
  }, []);

  const handleScanUpdate = useCallback(
    (err, result) => {
      if (!cameraReady) {
        setCameraReady(true);
      }

      if (result) {
        setShowCamera(false);
        setForm(prevForm => ({
          ...prevForm,
          [activeField]: result.getText(),
        }));
      }
    },
    [activeField, cameraReady]
  );

  const validateForm = () => {
    const {
      isValid,
      errors: validationErrors,
      jobNumberMissing,
    } = validateLinkDeviceForm({
      deviceId: form.deviceId,
      deviceType: form.deviceType,
      jobNumber: context.jobNumber,
    });

    setErrors(validationErrors);
    setIsJobNumberMissing(jobNumberMissing);

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const input = buildLinkDeviceInput(form, context);

    try {
      const { data } = await mutate({ variables: { input } });
      resetForm();
      setSubmitResult(data?.linkJobToDevice === true);
    } catch {
      setSubmitResult(false);
    }
  };

  const handleOpenScanner = fieldName => {
    setActiveField(fieldName);
    setShowCamera(true);
  };

  const handleCloseScanner = () => {
    setShowCamera(false);
  };

  return (
    <Flex1 className={styles.linkDeviceToJobPage}>
      <TitleWithExtra titleId="linkDeviceToJob.title" />

      <div className={styles.jobDetails}>
        <span>
          <FormattedMessage id="linkDeviceToJob.jobNumber" />
        </span>
        <span>{context.jobNumber}</span>
      </div>

      {showCamera ? (
        <div className={styles.scannerContainer}>
          <BarcodeScanner torchOn={torchOn} onScanUpdate={handleScanUpdate} />
          <ScannerHints />
        </div>
      ) : (
        <DeviceForm
          form={form}
          errors={errors}
          deviceTypeOptions={deviceTypeOptions}
          onFormChange={setForm}
          onOpenScanner={handleOpenScanner}
        />
      )}

      <Button
        type="primary"
        onClick={showCamera ? handleCloseScanner : handleSubmit}
      >
        <FormattedMessage id={showCamera ? 'buttons.cancel' : 'buttons.save'} />
      </Button>

      {loading && (
        <ProcessingModal textId="linkDeviceToJob.linking-job-to-device" />
      )}

      {submitResult && (
        <SuccessDialog
          titleId="linkDeviceToJob.successDialog.title"
          subTitleId="linkDeviceToJob.successDialog.subtitle"
          onOk={() => setSubmitResult(undefined)}
        />
      )}

      <SimpleConfirmDialog
        open={submitResult === false}
        onClose={() => setSubmitResult(undefined)}
        cancelTextId="buttons.close"
        textId="linkDeviceToJob.errorDialog.message"
        centered
        mask
      />

      <SimpleConfirmDialog
        open={isJobNumberMissing}
        onClose={() => setIsJobNumberMissing(false)}
        cancelTextId="buttons.close"
        textId="linkDeviceToJob.missingJobNumber.message"
        centered
        mask
      />
    </Flex1>
  );
}
